import React, { useMemo } from 'react';
import {
  Alert,
  Box,
  Skeleton,
  Stack,
  TablePagination,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import PanelLayout from '../CommanderOffline/PanelLayout';
import useTypeEntity from '../../../useTypeEntity';
import { M_SECONDS_POOLING_INTERVAL_DATA } from '../../../../../../../../service/store/Store.constants';
import useTypeRequest from '../../useTypeRequest';
import useStructures from '../../../../../../../../redux/structure/hooks/useStructures';
import { toBase64 } from '../../../../../../../../shared/util/base64';
import { getGroupsFromDeviceIdsGroupDictionary } from '../../../../../../../../redux/structure/selectors/Group.selector';
import useWatchError from '../../../../../../../../shared/hooks/useWatchError';
import StructureListItem from '../../../StructureListItem';
import { GatewayErrorIcon } from '../../../../../../../../assets/dashboard';
import { BusinessLayerGroupType } from '../../../../../../../../redux/structure/util/getTypeFromGroup';
import { useGetTypeFromGroup } from '../../../../../../../../redux/structure/util/useGetTypeFromGroup';
import useAirConnectStatus from '../../../../../../../../service/device/CommanderAPI/hooks/useAirConnectStatus';
import getGatewayErrors from '../../../../../../../../service/device/CommanderAPI/util/getGatewayErrors';

function GatewayErrorTableList() {
  const { groupId, marketId } = useTypeEntity({
    pollingInterval: M_SECONDS_POOLING_INTERVAL_DATA,
  });
  const { t } = useTranslation();
  const getTypeFromGroup = useGetTypeFromGroup(marketId);
  const typeRequest = useTypeRequest(marketId, groupId);
  const {
    data: dataAirConnectStatus,
    isLoading: isLoadingDataAirConnect,
    error: errorAirConnect,
  } = useAirConnectStatus({
    groupId,
    workspaceId: marketId,
    typeRequest,
  });

  const {
    data: structuresData,
    isLoading: isLoadingStructures,
    error: errorStructures,
    originalArgs: structuresOriginalArgs,
  } = useStructures({
    typeRequest,
    groupId,
    workspaceId: marketId,
  });

  const childGroups = useMemo(
    () =>
      structuresData?.flatMap((structure, i) =>
        (groupId
          ? structure.entities?.groups[toBase64(groupId)]?.childGroups ?? []
          : structure.result
        )
          .flatMap((groupId) => {
            const currentWorkspaceId = structuresOriginalArgs[i] ?? marketId;
            const groupsWithDeviceError = getGroupsFromDeviceIdsGroupDictionary(
              structure.entities?.groups ?? {},
              getGatewayErrors(dataAirConnectStatus)?.map(
                (device) => device.deviceID
              ) ?? [],
              groupId
            );
            return groupsWithDeviceError.filter(
              (group) =>
                getTypeFromGroup(group, currentWorkspaceId) ===
                BusinessLayerGroupType.Store
            );
          })
          .map((group) => ({
            ...group,
            workspaceId: structuresOriginalArgs[i] ?? marketId,
          }))
      ),

    [
      dataAirConnectStatus,
      getTypeFromGroup,
      groupId,
      marketId,
      structuresData,
      structuresOriginalArgs,
    ]
  );
  const loading = isLoadingDataAirConnect || isLoadingStructures;
  const error = errorAirConnect || errorStructures;

  useWatchError(error);

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
  };

  const childGroupsPaginated = useMemo(
    () =>
      childGroups?.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
      ) ?? [],
    [childGroups, page, rowsPerPage]
  );

  const content = loading ? (
    <Skeleton variant="rectangular" width={'100%'} height={200} />
  ) : (
    <Box mt={2}>
      <Typography variant="body1" gutterBottom>
        {t('dashboard.gateway_error.description_list')}
      </Typography>
      {childGroupsPaginated.length === 0 ? (
        <Alert severity="info">{t('dashboard.gateway_error.no_stores')}</Alert>
      ) : (
        <>
          <Stack spacing={1} mt={2}>
            {childGroupsPaginated.map((entity: any) => (
              <StructureListItem key={entity?.id} entity={entity} />
            ))}
          </Stack>
          <TablePagination
            component="div"
            count={childGroups?.length ?? 0}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </>
      )}
    </Box>
  );
  return (
    <PanelLayout
      innerContainerMaxWidth={'xl'}
      subPages={[
        {
          name: t('dashboard.gateway_error.title'),
          url: '',
        },
      ]}
      title={t('dashboard.gateway_error.title')}
      icon={<GatewayErrorIcon />}
    >
      {content}
    </PanelLayout>
  );
}

export default GatewayErrorTableList;
